// messageCenter/ticketCreateView.js — the form "new ticket": title, kind, priority, area, origin, text, files, and the
// assignee for managers. Stateful component (text editor and files keep their state); renders from { canManage,
// assignees, saving, error }, checks the two required fields itself and reports values and files via onSubmit.
// Screenshots can be pasted or dropped anywhere in the form. Used on the inbox page (link back to the list) and in the
// quick report dialog on every backend page (onCancel instead of the link, own heading).
// areaSource: the areas in use for the field „Bereich“ (areaInput.js); without it the field is a plain input.
import '../../../stylesheet_vite/src/modules/messagecenter-form.css';
import { str } from '../i18n/str.js';
import { createDisposers } from '../core/disposers.js';
import { createTextEditor } from './textEditor.js';
import { createAreaInput } from './areaInput.js';
import { KIND_LABEL_KEYS, PRIORITY_LABEL_KEYS, labelOf } from './ticketLabels.js';
import { createAttachmentPicker } from './attachmentPicker.js';

export function createTicketCreateView({ entities, backHref = '', limits, onSubmit, onCancel = null, headingKey = 'MC_NEW_TICKET', shrink,
                                         raw = false, areaSource = null }) {
    const disposers = createDisposers();
    const element = document.createElement('div');
    element.className = 'ticketCreate';

    const back = document.createElement('a');
    back.className = 'ticketCreate__back';
    back.href = backHref;
    back.textContent = '‹ ' + str('MC_BACK_TO_LIST');

    const heading = document.createElement('h2');   // headingKey '' = no heading (the quick report window has its own title bar)
    heading.textContent = headingKey ? str(headingKey) : '';
    heading.hidden = !headingKey;

    const message = document.createElement('p');
    message.className = 'ticketCreate__message';

    const title = textInput();
    const kind = choice(KIND_LABEL_KEYS, 'bug');
    const priority = choice(PRIORITY_LABEL_KEYS, 'normal');
    const areaInput = createAreaInput({ source: areaSource });
    disposers.add(() => areaInput.destroy());
    const area = areaInput.element;
    const origin = textInput(str('MC_ORIGIN_PLACEHOLDER'));
    const assignee = document.createElement('select');
    const assigneeField = field('MC_COL_ASSIGNEE', assignee);

    const editor = createTextEditor({ entities, rows: 6, placeholder: str('MC_TEXT_PLACEHOLDER'), raw });
    disposers.add(() => editor.destroy());
    const picker = createAttachmentPicker({ limits, ...(shrink ? { shrink } : {}) });
    disposers.add(() => picker.destroy());

    const submit = document.createElement('button');
    submit.type = 'button';
    submit.className = 'topo2-btn topo2-btn--small ticketCreate__submit';
    submit.textContent = str('MC_CREATE');
    submit.addEventListener('click', async () => {
        await picker.whenReady();   // pasted images may still be shrinking
        const values = {
            title: title.value.trim(),
            body: editor.getValue(),
            ticket_kind: kind.value,
            priority: priority.value,
            area: area.value.trim(),
            origin: origin.value.trim(),
        };
        if (!assigneeField.hidden && assignee.value !== '') { values.assignee_id = assignee.value; }
        if (values.title === '' || values.body.trim() === '') {
            showMessage('MC_CREATE_REQUIRED');
            return;
        }
        onSubmit(values, picker.getFiles());
    });

    const titleField = field('MC_COL_TITLE', title);
    titleField.classList.add('ticketCreate__field--wide');
    const fields = document.createElement('div');
    fields.className = 'ticketCreate__fields';
    fields.append(titleField, field('MC_FILTER_KIND', kind), field('MC_FILTER_PRIORITY', priority), field('MC_COL_AREA', area),
        field('MC_FIELD_ORIGIN', origin), assigneeField);
    // a div, not a label: the editor is no labelable control, so a click into the text would go to the raw checkbox
    const textField = field('MC_FIELD_TEXT', editor.element, 'div');
    textField.classList.add('ticketCreate__text');

    const actions = document.createElement('div');
    actions.className = 'ticketCreate__actions';
    actions.append(submit);
    if (onCancel) {
        const cancel = document.createElement('button');
        cancel.type = 'button';
        cancel.className = 'topo2-btn topo2-btn--small ticketCreate__cancel';
        cancel.textContent = str('MC_CANCEL');
        cancel.addEventListener('click', () => onCancel());
        actions.append(cancel);
    }

    element.append(...(onCancel ? [] : [back]), heading, message, fields, textField, picker.element, actions);
    picker.attachTo(element);   // paste / drop anywhere in the form

    function update({ canManage = false, assignees = [], saving = false, error = '' }) {
        assigneeField.hidden = !canManage;
        if (canManage && assignee.options.length !== assignees.length + 1) {
            const current = assignee.value;
            assignee.replaceChildren(option('', '—'), ...assignees.map((a) => option(String(a.user_id), a.name ?? '#' + a.user_id)));
            assignee.value = current;
        }
        submit.disabled = saving;
        element.classList.toggle('is-saving', saving);
        showMessage(error);
    }

    function showMessage(key) {
        message.textContent = key ? str(key) : '';
        message.hidden = !key;
    }

    /** Empty form for the next ticket. */
    function reset() {
        for (const input of [title, area, origin]) { input.value = ''; }
        kind.value = 'bug';
        priority.value = 'normal';
        assignee.value = '';
        editor.setValue('');
        picker.clear();
        showMessage('');
    }

    function destroy() {
        disposers.dispose();
        element.remove();
    }

    return { element, update, reset, destroy };
}

function textInput(placeholder = '') {
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'topo2-field-textinput topo2-field--bordered';   // bordered: a white field on the white page
    input.placeholder = placeholder;
    return input;
}

function choice(labelKeys, value) {
    const select = document.createElement('select');
    for (const key of Object.keys(labelKeys)) { select.append(option(key, labelOf(labelKeys, key))); }
    select.value = value;
    return select;
}

function field(key, control, tag = 'label') {
    const label = document.createElement(tag);
    label.className = 'ticketCreate__field';
    const caption = document.createElement('span');
    caption.className = 'topo2-field-label';
    caption.textContent = str(key);
    label.append(caption, control);
    return label;
}

function option(value, text) {
    const o = document.createElement('option');
    o.value = value;
    o.textContent = text;
    return o;
}
